const DEFAULT_BASE = 'https://booking-api-production-845a.up.railway.app';

export const API_BASE =
  (process.env.REACT_APP_API_BASE || DEFAULT_BASE).replace(/\/$/, '');

export class ApiError extends Error {
  constructor(message) {
    super(message);
    this.name = 'ApiError';
  }
}

async function decode(res) {
  let body = {};
  const text = await res.text();
  if (text) {
    try {
      body = JSON.parse(text);
    } catch {
      body = { error: text };
    }
  }
  if (res.ok) return body;
  throw new ApiError(
    body.error?.toString() ||
      body.detail?.toString() ||
      `Request failed (${res.status})`
  );
}

async function send(path, options = {}, { timeoutMs = 30000, retries = 2 } = {}) {
  const url = `${API_BASE}${path}`;
  let lastErr;
  for (let attempt = 0; attempt <= retries; attempt += 1) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    try {
      const res = await fetch(url, { ...options, signal: controller.signal });
      clearTimeout(timer);
      return await decode(res);
    } catch (err) {
      clearTimeout(timer);
      lastErr = err;
      if (err instanceof ApiError) throw err;
      if (attempt >= retries) break;
      await new Promise((r) => setTimeout(r, 400 * (attempt + 1)));
    }
  }
  if (lastErr?.name === 'AbortError') {
    throw new ApiError('Server is slow to respond. Please try again.');
  }
  throw new ApiError(
    'Cannot reach booking server right now. Please try again in a moment.'
  );
}

export async function searchTrips({ origin, destination, date, passengers }) {
  const qs = new URLSearchParams({
    origin,
    destination,
    date,
    passengers: String(passengers),
  });
  const data = await send(`/api/trips?${qs}`, {}, { timeoutMs: 60000 });
  if (!Array.isArray(data.trips)) {
    throw new ApiError('Unexpected trips response from server');
  }
  return data.trips;
}

export async function createBooking({
  trip,
  seats,
  passengers,
  contact,
  pickupPoint,
  dropoffPoint,
}) {
  const data = await send(
    '/api/bookings',
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        trip,
        seats,
        passengers,
        contact,
        ...(pickupPoint ? { pickupPoint } : {}),
        ...(dropoffPoint ? { dropoffPoint } : {}),
      }),
    },
    { timeoutMs: 60000, retries: 1 }
  );
  return data.booking;
}

export async function listPayments(tripId) {
  const data = await send(
    `/api/bookings/${encodeURIComponent(tripId)}/payments`,
    {},
    { timeoutMs: 45000 }
  );
  return Array.isArray(data.items) ? data.items : [];
}

export async function payBooking({ tripId, method, phoneOverride, token }) {
  const data = await send(
    `/api/bookings/${encodeURIComponent(tripId)}/pay`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        method,
        ...(phoneOverride ? { phoneOverride } : {}),
        ...(token ? { token } : {}),
      }),
    },
    // Never retry pay — a timeout after SafariPlus accepts can double-push.
    { timeoutMs: 60000, retries: 0 }
  );
  return data.booking;
}

export async function paymentStatus(tripId) {
  return send(
    `/api/bookings/${encodeURIComponent(tripId)}/status`,
    {},
    { timeoutMs: 30000 }
  );
}

export async function cancelBooking(tripId) {
  await send(
    `/api/bookings/${encodeURIComponent(tripId)}`,
    { method: 'DELETE' },
    { timeoutMs: 30000, retries: 1 }
  );
}

// --- Optional passenger accounts + cloud tickets ---

function authHeaders(token) {
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export async function authRegister({ phone, password, name, email, ageGroup }) {
  return send(
    '/api/auth/register',
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ phone, password, name, email, ageGroup }),
    },
    { timeoutMs: 30000, retries: 1 }
  );
}

export async function authLogin({ phone, password, email, identifier }) {
  return send(
    '/api/auth/login',
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        password,
        ...(identifier ? { identifier } : {}),
        ...(phone ? { phone } : {}),
        ...(email ? { email } : {}),
      }),
    },
    { timeoutMs: 30000, retries: 1 }
  );
}

export async function authLogout(token) {
  await send(
    '/api/auth/logout',
    {
      method: 'POST',
      headers: { ...authHeaders(token) },
    },
    { timeoutMs: 15000, retries: 0 }
  );
}

export async function authMe(token) {
  return send(
    '/api/auth/me',
    { headers: { ...authHeaders(token) } },
    { timeoutMs: 20000, retries: 1 }
  );
}

export async function listCloudTickets(token) {
  const data = await send(
    '/api/me/tickets',
    { headers: { ...authHeaders(token) } },
    { timeoutMs: 30000, retries: 1 }
  );
  return Array.isArray(data.tickets) ? data.tickets : [];
}

export async function saveCloudTicket(token, booking) {
  const data = await send(
    '/api/me/tickets',
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...authHeaders(token),
      },
      body: JSON.stringify({ booking }),
    },
    { timeoutMs: 30000, retries: 1 }
  );
  return data.booking;
}

export async function deleteCloudTicket(token, reference) {
  await send(
    `/api/me/tickets/${encodeURIComponent(reference)}`,
    {
      method: 'DELETE',
      headers: { ...authHeaders(token) },
    },
    { timeoutMs: 20000, retries: 1 }
  );
}

export function formatMoney(amount, currency = 'TZS') {
  const n = Number(amount) || 0;
  try {
    return new Intl.NumberFormat('en-TZ', {
      style: 'currency',
      currency,
      maximumFractionDigits: 0,
    }).format(n);
  } catch {
    return `${currency} ${n.toLocaleString()}`;
  }
}
